/**
 * 人民团结阵线程序化低模。
 *
 * 建筑语言：新粗野主义——清水混凝土、外露管道、几何方正、阵营色标语带。
 * 单位：制式方肩剪影。owner 0 铁锈红 / owner 1 青蓝。
 */

import { MeshBuilder, type Mesh } from './mesh.ts';

interface Faction {
  main: number;
  light: number;
  dark: number;
  paint: number;
}

function faction(owner: number): Faction {
  return owner === 0
    ? { main: 0xc8412f, light: 0xe0705c, dark: 0x6e2014, paint: 0xa33424 }
    : { main: 0x2f7fc8, light: 0x5ca8e8, dark: 0x163e6c, paint: 0x245a96 };
}

const C = {
  conc: 0xb4ab9c,
  concMid: 0x948c7e,
  concDark: 0x6f685c,
  concDeep: 0x4e493f,
  steel: 0x6a727c,
  steelDark: 0x3e464e,
  pipe: 0x4a5560,
  rust: 0x8a4a32,
  warn: 0xc9a227,
  glass: 0x1a242c,
  dirt: 0x5a5246,
  gold: 0xd4b45a,
  ore: 0xe0b44a,
  black: 0x161412,
  sand: 0xc4b48a,
};

function pipes(b: MeshBuilder, hex: number, xs: number[], y: number, z: number, r: number, h: number): void {
  for (const x of xs) {
    b.push().move(x, y, z).cylinder(r, r, h, hex, 6).pop();
  }
}

function banner(b: MeshBuilder, f: Faction, w: number, y: number, z: number): void {
  b.push().move(0, y, z).box(w, 0.08, 0.06, f.main).pop();
  b.push().move(0, y - 0.02, z + 0.01).box(w * 0.55, 0.05, 0.04, f.light).pop();
}

function windows(b: MeshBuilder, cols: number, rows: number, originX: number, originY: number, z: number, gapX: number, gapY: number): void {
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      b.push()
        .move(originX + c * gapX, originY + r * gapY, z)
        .box(0.12, 0.16, 0.04, C.glass)
        .pop();
    }
  }
}

export function buildUnitMesh(key: string, owner: number, cargo = false, walkFrame = 0): Mesh {
  const f = faction(owner);
  switch (key) {
    case 'conyard':
      return conyard(f);
    case 'reactor':
      return reactor(f);
    case 'refinery':
      return refinery(f);
    case 'barracks':
      return barracks(f);
    case 'pillbox':
      return pillbox(f, walkFrame);
    case 'factory':
      return factory(f);
    case 'radar':
      return radar(f);
    case 'turret':
      return turret(f, walkFrame);
    case 'conscript':
      return infantry(f, 'rifle', false, walkFrame);
    case 'engineer':
      return infantry(f, 'tool', true, walkFrame);
    case 'rifleman':
      return infantry(f, 'rifle', true, walkFrame);
    case 'stormtrooper':
      return infantry(f, 'rpg', true, walkFrame);
    case 'guardsman':
      return infantry(f, 'at', true, walkFrame);
    case 'harvester':
      return harvester(f, cargo, walkFrame);
    case 'apc':
      return apc(f, walkFrame);
    case 'tank':
      return tank(f, walkFrame);
    case 'artillery':
      return artillery(f, walkFrame);
    case 'mech':
      return mech(f, walkFrame);
    default:
      return fallback(f);
  }
}

function conyard(f: Faction): Mesh {
  const b = new MeshBuilder();
  b.box(2.7, 0.14, 2.7, C.concDark);
  b.push().move(0, 0.14, 0).box(2.5, 0.08, 2.5, C.concMid).pop();
  // 中央塔楼
  b.push().move(0, 0.22, 0).box(1.15, 1.55, 1.15, C.conc).pop();
  b.push().move(0, 1.77, 0).box(1.28, 0.12, 1.28, C.concDark).pop();
  b.push().move(0, 1.89, 0).box(0.7, 0.45, 0.7, C.steel).pop();
  b.push().move(0, 2.34, 0).cylinder(0.08, 0.08, 0.55, f.main, 6).pop();
  // 两翼
  b.push().move(-0.95, 0.22, 0.15).box(0.7, 0.72, 1.9, C.concMid).pop();
  b.push().move(0.95, 0.22, 0.15).box(0.7, 0.72, 1.9, C.concMid).pop();
  windows(b, 3, 2, -0.95, 0.42, 1.12, 0.22, 0.28);
  windows(b, 3, 2, 0.95, 0.42, 1.12, 0.22, 0.28);
  // 烟囱与管道
  b.push().move(1.05, 0.94, -0.7).cylinder(0.16, 0.13, 1.1, C.pipe, 8).pop();
  b.push().move(1.05, 2.04, -0.7).cylinder(0.18, 0.1, 0.18, C.steelDark, 8).pop();
  pipes(b, C.pipe, [-0.4, -0.22], 0.22, -1.2, 0.06, 0.9);
  banner(b, f, 1.8, 1.55, 0.6);
  // 门口
  b.push().move(0, 0.22, 1.22).box(0.55, 0.5, 0.12, C.glass).pop();
  b.push().move(0, 0.22, 1.28).box(0.62, 0.06, 0.08, f.paint).pop();
  return b.mesh;
}

function reactor(f: Faction): Mesh {
  const b = new MeshBuilder();
  b.box(1.85, 0.12, 1.85, C.concDark);
  b.push().move(0, 0.12, 0.15).box(1.2, 0.7, 1.15, C.conc).pop();
  // 双冷却塔
  b.push().move(-0.45, 0.82, -0.15).cylinder(0.38, 0.22, 1.15, C.concMid, 10).pop();
  b.push().move(0.45, 0.82, -0.15).cylinder(0.38, 0.22, 1.15, C.concMid, 10).pop();
  b.push().move(-0.45, 1.95, -0.15).cylinder(0.24, 0.2, 0.12, C.concDeep, 8).pop();
  b.push().move(0.45, 1.95, -0.15).cylinder(0.24, 0.2, 0.12, C.concDeep, 8).pop();
  // 警示条与管道
  b.push().move(0, 0.42, 0.74).box(1.15, 0.1, 0.06, C.warn).pop();
  b.push().move(0, 0.52, 0.74).box(1.15, 0.08, 0.06, C.black).pop();
  b.push().move(0.7, 0.12, 0.55).cylinder(0.07, 0.07, 0.55, C.pipe, 6).pop();
  b.push().move(-0.7, 0.12, 0.55).cylinder(0.07, 0.07, 0.55, C.pipe, 6).pop();
  banner(b, f, 0.9, 0.78, 0.72);
  b.push().move(0, 0.82, 0.2).box(0.35, 0.22, 0.35, C.steel).pop();
  return b.mesh;
}

function refinery(f: Faction): Mesh {
  const b = new MeshBuilder();
  b.box(2.6, 0.12, 2.4, C.concDark);
  b.push().move(-0.55, 0.12, 0.1).box(1.35, 0.7, 1.7, C.conc).pop();
  // 蒸馏塔
  b.push().move(0.85, 0.12, -0.35).cylinder(0.32, 0.28, 1.7, C.steel, 9).pop();
  b.push().move(0.85, 1.82, -0.35).cylinder(0.22, 0.18, 0.35, C.steelDark, 8).pop();
  // 卧式储罐
  b.push().move(0.7, 0.42, 0.7).tankZ(0.28, 1.15, C.gold, 8).pop();
  b.push().move(0.7, 0.42, 1.05).tankZ(0.22, 0.9, mixOre(), 8).pop();
  // 卸货台
  b.push().move(-0.55, 0.12, 1.05).box(1.1, 0.28, 0.45, C.concMid).pop();
  b.push().move(-0.55, 0.4, 1.15).box(0.7, 0.18, 0.2, f.paint).pop();
  pipes(b, C.pipe, [0.35, 0.5], 0.12, -0.9, 0.055, 1.1);
  banner(b, f, 1.1, 0.78, 0.95);
  b.push().move(-0.9, 0.82, -0.4).box(0.35, 0.45, 0.35, C.steel).pop();
  return b.mesh;
}

function mixOre(): number {
  return C.ore;
}

function barracks(f: Faction): Mesh {
  const b = new MeshBuilder();
  b.box(1.85, 0.1, 1.85, C.concDark);
  b.push().move(0, 0.1, -0.1).box(1.7, 0.95, 1.35, C.conc).pop();
  b.push().move(0, 1.05, -0.1).wedge(1.78, 0.32, 1.4, C.concDark).pop();
  windows(b, 4, 2, -0.52, 0.32, 0.6, 0.34, 0.32);
  // 围墙 / 操场
  b.push().move(0, 0.1, 0.78).box(1.7, 0.22, 0.12, C.concMid).pop();
  b.push().move(0, 0.1, 0.78).box(0.4, 0.38, 0.14, C.glass).pop();
  b.push().move(0.7, 0.1, 0.55).cylinder(0.04, 0.04, 0.85, C.steelDark, 5).pop();
  b.push().move(0.7, 0.95, 0.55).box(0.28, 0.16, 0.05, f.main).pop();
  banner(b, f, 1.2, 0.95, 0.58);
  return b.mesh;
}

function recoilOf(frame: number, mag: number): number {
  return frame === 1 ? mag : 0;
}

function trackOf(frame: number): number {
  if (frame === 2) return 0.08;
  if (frame === 3) return -0.08;
  return 0;
}

function wrapSpan(t: number, span: number): number {
  const s = ((t % span) + span) % span;
  return s;
}

function treads(b: MeshBuilder, x: number, y: number, z: number, len: number, shift: number): void {
  b.push().move(x, y, z).box(0.16, 0.2, len, C.black).pop();
  const span = len * 0.42;
  for (let i = -2; i <= 2; i++) {
    const tz = z + wrapSpan(i * 0.18 + shift, span) - len * 0.22;
    b.push().move(x, y + 0.11, tz).box(0.18, 0.05, 0.08, C.steelDark).pop();
  }
  for (const wz of [-len * 0.32, 0, len * 0.32]) {
    b.push().move(x, y + 0.02, z + wz + shift * 0.4).cylinder(0.07, 0.07, 0.14, C.steel, 6).pop();
  }
}

function muzzleFlash(b: MeshBuilder, x: number, y: number, z: number, big: boolean): void {
  b.push().move(x, y, z).cylinder(big ? 0.14 : 0.09, 0.02, big ? 0.18 : 0.12, 0xffe8a0, 6).pop();
  b.push().move(x, y, z + 0.04).box(big ? 0.22 : 0.14, 0.03, 0.04, 0xfff6c8, true).pop();
}

function pillbox(f: Faction, frame = 0): Mesh {
  const rec = recoilOf(frame, 0.12);
  const b = new MeshBuilder();
  b.box(0.95, 0.08, 0.95, C.dirt);
  b.push().move(0, 0.08, 0).cylinder(0.42, 0.4, 0.38, C.concMid, 8).pop();
  b.push().move(0, 0.46, 0).cylinder(0.4, 0.36, 0.1, C.concDark, 8).pop();
  b.push().move(0, 0.28, 0.4).box(0.38, 0.1, 0.08, C.black).pop();
  b.push().move(0, 0.3, 0.55 - rec).box(0.06, 0.06, 0.42, C.steelDark, true).pop();
  if (frame === 1) muzzleFlash(b, 0, 0.3, 0.82 - rec, false);
  b.push().move(-0.38, 0.08, 0.28).box(0.22, 0.16, 0.28, C.sand).pop();
  b.push().move(0.38, 0.08, 0.28).box(0.22, 0.16, 0.28, C.sand).pop();
  b.push().move(0, 0.52, 0).cylinder(0.08, 0.08, 0.12, f.main, 6).pop();
  return b.mesh;
}

function factory(f: Faction): Mesh {
  const b = new MeshBuilder();
  b.box(2.7, 0.12, 2.5, C.concDark);
  b.push().move(0, 0.12, 0).box(2.45, 1.15, 2.15, C.conc).pop();
  b.push().move(0, 1.27, 0).box(2.55, 0.12, 2.25, C.concDark).pop();
  for (const z of [-0.55, 0, 0.55]) {
    b.push().move(0, 1.39, z).box(2.1, 0.18, 0.28, C.steel).pop();
  }
  b.push().move(0, 0.12, 1.1).box(1.15, 0.85, 0.08, C.steelDark).pop();
  b.push().move(0, 0.55, 1.14).box(1.18, 0.08, 0.06, f.paint).pop();
  b.push().move(-0.2, 1.5, 0.2).box(0.12, 0.12, 1.6, C.steel).pop();
  b.push().move(-0.2, 1.35, 0.9).box(0.08, 0.28, 0.08, C.steelDark).pop();
  pipes(b, C.pipe, [1.05, 1.2], 1.39, -0.7, 0.07, 0.55);
  banner(b, f, 1.6, 1.15, 1.08);
  b.push().move(1.0, 0.12, -0.85).box(0.4, 0.7, 0.45, C.concMid).pop();
  return b.mesh;
}

function radar(f: Faction): Mesh {
  const b = new MeshBuilder();
  b.box(1.7, 0.1, 1.7, C.concDark);
  b.push().move(0, 0.1, 0).box(0.7, 1.15, 0.7, C.conc).pop();
  b.push().move(0, 1.25, 0).box(0.55, 0.18, 0.55, C.steel).pop();
  b.push().move(0, 1.43, 0).cylinder(0.05, 0.05, 0.55, C.steelDark, 6).pop();
  b.push().move(0, 1.7, 0.05).pitch(-0.7).cylinder(0.55, 0.08, 0.16, C.steel, 12, true).pop();
  b.push().move(0, 1.62, 0.18).box(0.05, 0.05, 0.35, C.steelDark, true).pop();
  windows(b, 1, 3, 0, 0.35, 0.38, 0, 0.28);
  banner(b, f, 0.65, 1.12, 0.38);
  b.push().move(0.55, 0.1, 0.55).box(0.35, 0.4, 0.35, C.concMid).pop();
  b.push().move(-0.55, 0.1, 0.45).cylinder(0.08, 0.08, 0.7, C.pipe, 6).pop();
  return b.mesh;
}

function turret(f: Faction, frame = 0): Mesh {
  const rec = recoilOf(frame, 0.2);
  const b = new MeshBuilder();
  b.box(0.95, 0.08, 0.95, C.concDark);
  b.push().move(0, 0.08, 0).cylinder(0.38, 0.36, 0.22, C.conc, 8).pop();
  b.push().move(0, 0.3, 0).box(0.55, 0.32, 0.5, C.steel).pop();
  b.push().move(0, 0.42, 0.55 - rec).box(0.1, 0.1, 0.7, C.steelDark, true).pop();
  b.push().move(0, 0.42, 0.95 - rec).cylinder(0.07, 0.05, 0.12, C.black, 6).pop();
  if (frame === 1) muzzleFlash(b, 0, 0.42, 1.12 - rec, true);
  b.push().move(0, 0.62, 0).box(0.22, 0.12, 0.22, f.paint).pop();
  b.push().move(0, 0.08, 0.42).box(0.7, 0.1, 0.12, C.concMid).pop();
  return b.mesh;
}

function infantry(f: Faction, weapon: 'rifle' | 'tool' | 'rpg' | 'at', helmet: boolean, walkFrame = 0): Mesh {
  const b = new MeshBuilder();
  // walkFrame 0 = 站立；1–4 = 走步循环（左迈 → 交换 → 右迈 → 交换）
  const gait = [
    { l: 0.02, r: -0.02, liftL: 0, liftR: 0, armL: 0, armR: 0, bob: 0, lean: 0 },
    { l: 0.13, r: -0.11, liftL: 0, liftR: 0.07, armL: -0.08, armR: 0.1, bob: 0.01, lean: 0.03 },
    { l: 0.05, r: -0.04, liftL: 0.09, liftR: 0.02, armL: -0.04, armR: 0.04, bob: 0.045, lean: 0.04 },
    { l: -0.11, r: 0.13, liftL: 0.07, liftR: 0, armL: 0.1, armR: -0.08, bob: 0.01, lean: 0.03 },
    { l: -0.04, r: 0.05, liftL: 0.02, liftR: 0.09, armL: 0.04, armR: -0.04, bob: 0.045, lean: 0.04 },
  ];
  const g = gait[Math.max(0, Math.min(4, walkFrame | 0))]!;
  const hip = 0.28 + g.bob;

  const lH = Math.max(0.16, hip - g.liftL);
  const rH = Math.max(0.16, hip - g.liftR);
  b.push().move(-0.07, g.liftL, g.l).box(0.09, lH, 0.1, f.dark).pop();
  b.push().move(0.07, g.liftR, g.r).box(0.09, rH, 0.1, f.dark).pop();

  b.push().move(0, hip, g.lean).box(0.28, 0.32, 0.2, f.main).pop();
  b.push().move(0, hip + 0.14, g.lean + 0.11).box(0.3, 0.08, 0.04, f.paint).pop();
  b.push().move(-0.18, hip + 0.22, g.lean + g.armL * 0.4).box(0.1, 0.1, 0.18, f.dark).pop();
  b.push().move(0.18, hip + 0.22, g.lean + g.armR * 0.4).box(0.1, 0.1, 0.18, f.dark).pop();

  const headY = hip + 0.32;
  b.push().move(0, headY, g.lean).cylinder(0.09, 0.09, 0.14, 0xc4a07a, 7).pop();
  if (helmet) {
    b.push().move(0, headY + 0.1, g.lean).cylinder(0.11, 0.1, 0.08, f.dark, 7).pop();
    b.push().move(0, headY + 0.08, g.lean + 0.08).box(0.16, 0.05, 0.04, C.steelDark).pop();
  }

  const wy = hip + 0.12;
  const wz = 0.18 + g.lean + g.armR * 0.5;
  if (weapon === 'rifle') {
    b.push().move(0.16, wy, wz).box(0.05, 0.05, 0.42, C.steelDark, true).pop();
  } else if (weapon === 'tool') {
    b.push().move(0.16, wy, 0.12 + g.lean).box(0.06, 0.22, 0.06, C.warn, true).pop();
    b.push().move(-0.12, wy, -0.08 + g.lean).box(0.16, 0.18, 0.12, C.gold).pop();
  } else if (weapon === 'rpg') {
    b.push().move(0.12, wy + 0.04, wz + 0.04).cylinder(0.06, 0.06, 0.38, C.rust, 6).pop();
  } else {
    b.push().move(0.14, wy, wz).box(0.07, 0.07, 0.5, C.steel, true).pop();
    b.push().move(0.14, wy, 0.02 + g.lean).box(0.1, 0.1, 0.12, C.steelDark, true).pop();
  }
  return b.mesh;
}

function harvester(f: Faction, cargo: boolean, frame = 0): Mesh {
  const tr = trackOf(frame);
  const b = new MeshBuilder();
  b.push().move(0, 0, 0).box(0.72, 0.12, 1.15, C.steelDark).pop();
  treads(b, -0.4, 0.02, 0, 1.2, tr);
  treads(b, 0.4, 0.02, 0, 1.2, tr);
  b.push().move(0, 0.22, -0.05).box(0.7, 0.32, 0.85, f.main).pop();
  b.push().move(0, 0.54, -0.35).box(0.5, 0.28, 0.38, f.dark).pop();
  b.push().move(0, 0.64, -0.18).box(0.42, 0.16, 0.08, C.glass).pop();
  const hop = cargo ? C.gold : C.steel;
  b.push().move(0, 0.54, 0.28).box(0.55, cargo ? 0.38 : 0.22, 0.5, hop).pop();
  b.push().move(0, 0.18, 0.7 + tr * 0.4).box(0.62, 0.12, 0.22, C.steel).pop();
  b.push().move(0, 0.5, -0.35).box(0.52, 0.05, 0.06, f.light).pop();
  return b.mesh;
}

function apc(f: Faction, frame = 0): Mesh {
  const rec = recoilOf(frame, 0.1);
  const tr = trackOf(frame);
  const kick = rec > 0 ? -0.04 : 0;
  const b = new MeshBuilder();
  treads(b, -0.38, 0.04, 0, 1.05, tr);
  treads(b, 0.38, 0.04, 0, 1.05, tr);
  b.push().move(0, 0.16, kick).box(0.7, 0.32, 1.05, f.main).pop();
  b.push().move(0, 0.48, -0.15 + kick).box(0.62, 0.18, 0.55, f.dark).pop();
  b.push().move(0, 0.55, 0.12 + kick).box(0.5, 0.12, 0.08, C.glass).pop();
  b.push().move(0.12, 0.52, 0.35 - rec + kick).box(0.08, 0.08, 0.35, C.steelDark, true).pop();
  if (frame === 1) muzzleFlash(b, 0.12, 0.52, 0.58 - rec + kick, false);
  b.push().move(0, 0.18, 0.55 + kick).box(0.72, 0.08, 0.06, f.paint).pop();
  return b.mesh;
}

function tank(f: Faction, frame = 0): Mesh {
  const rec = recoilOf(frame, 0.22);
  const tr = trackOf(frame);
  const kick = rec > 0 ? -0.07 : 0;
  const b = new MeshBuilder();
  treads(b, -0.42, 0.04, 0, 1.15, tr);
  treads(b, 0.42, 0.04, 0, 1.15, tr);
  b.push().move(0, 0.18, kick).box(0.78, 0.32, 1.12, f.main).pop();
  b.push().move(0, 0.5, -0.05 + kick).cylinder(0.32, 0.32, 0.22, f.dark, 8).pop();
  b.push().move(0, 0.58, -0.05 + kick).box(0.42, 0.18, 0.48, f.paint).pop();
  b.push().move(0, 0.66, 0.55 - rec + kick).box(0.08, 0.08, 0.85, C.steelDark, true).pop();
  b.push().move(0, 0.66, 1.0 - rec + kick).cylinder(0.06, 0.05, 0.1, C.black, 6).pop();
  if (frame === 1) muzzleFlash(b, 0, 0.66, 1.16 - rec + kick, true);
  b.push().move(0, 0.22, 0.58 + kick).box(0.8, 0.08, 0.06, C.warn).pop();
  return b.mesh;
}

function artillery(f: Faction, frame = 0): Mesh {
  const rec = recoilOf(frame, 0.32);
  const tr = trackOf(frame);
  const kick = rec > 0 ? -0.1 : 0;
  const b = new MeshBuilder();
  treads(b, -0.4, 0.04, 0, 1.05, tr);
  treads(b, 0.4, 0.04, 0, 1.05, tr);
  b.push().move(0, 0.18, -0.1 + kick).box(0.74, 0.28, 0.9, f.main).pop();
  b.push().move(0, 0.46, 0.05 + kick).box(0.4, 0.22, 0.55, f.dark).pop();
  b.push().move(0, 0.58, 0.55 - rec + kick).pitch(-0.25 - rec * 0.4).box(0.1, 0.1, 1.35, C.steelDark, true).pop();
  if (frame === 1) muzzleFlash(b, 0, 0.82, 1.28 - rec + kick, true);
  b.push().move(0, 0.22, 0.4 + kick).box(0.76, 0.08, 0.06, f.paint).pop();
  return b.mesh;
}

function mech(f: Faction, frame = 0): Mesh {
  const rec = recoilOf(frame, 0.18);
  const stomp = frame === 2 ? 1 : frame === 3 ? -1 : 0;
  const b = new MeshBuilder();
  b.push().move(-0.28, stomp > 0 ? 0.06 : 0, 0.05 + stomp * 0.06).box(0.22, 0.55, 0.28, f.dark).pop();
  b.push().move(0.28, stomp < 0 ? 0.06 : 0, 0.05 - stomp * 0.06).box(0.22, 0.55, 0.28, f.dark).pop();
  b.push().move(-0.28, 0, 0.22 + stomp * 0.05).box(0.26, 0.12, 0.38, C.steelDark).pop();
  b.push().move(0.28, 0, 0.22 - stomp * 0.05).box(0.26, 0.12, 0.38, C.steelDark).pop();
  b.push().move(0, 0.55, 0).box(0.85, 0.55, 0.7, f.main).pop();
  b.push().move(0, 0.85, 0.1).box(0.7, 0.28, 0.55, f.paint).pop();
  b.push().move(0, 1.05, 0.05).box(0.4, 0.22, 0.4, f.dark).pop();
  b.push().move(0, 1.18, 0.22).box(0.28, 0.12, 0.08, C.glass).pop();
  b.push().move(-0.55, 0.95, 0.15).box(0.28, 0.22, 0.55, C.steel).pop();
  b.push().move(0.55, 0.95, 0.15).box(0.28, 0.22, 0.55, C.steel).pop();
  b.push().move(-0.55, 1.0, 0.55 - rec).box(0.1, 0.1, 0.55, C.steelDark, true).pop();
  b.push().move(0.55, 1.0, 0.55 - rec).box(0.1, 0.1, 0.55, C.steelDark, true).pop();
  if (frame === 1) {
    muzzleFlash(b, -0.55, 1.0, 0.9 - rec, false);
    muzzleFlash(b, 0.55, 1.0, 0.9 - rec, false);
  }
  banner(b, f, 0.6, 0.82, 0.38);
  return b.mesh;
}

function fallback(f: Faction): Mesh {
  const b = new MeshBuilder();
  b.box(0.8, 0.5, 0.8, f.main);
  return b.mesh;
}

/** 炮弹：穿甲杆、爆破弹、动能曳光。沿 +Z 飞。 */
export function buildShellMesh(dmgType: number): Mesh {
  const b = new MeshBuilder();
  if (dmgType === 2) {
    b.push().move(0, 0.07, 0).cylinder(0.09, 0.1, 0.2, 0xc45c28, 8).pop();
    b.push().move(0, 0.07, 0.16).cylinder(0.06, 0.02, 0.12, 0x3a342e, 6).pop();
    b.push().move(0, 0.07, -0.04).cylinder(0.11, 0.11, 0.06, 0xf0a050, 8).pop();
  } else if (dmgType === 1) {
    b.push().move(0, 0.05, -0.08).cylinder(0.04, 0.028, 0.42, 0xe0d090, 6).pop();
    b.push().move(0, 0.05, 0.3).cylinder(0.022, 0.006, 0.16, 0xfff6c8, 6).pop();
    b.push().move(0, 0.05, -0.12).box(0.08, 0.02, 0.06, 0x6a6050, true).pop();
  } else {
    b.push().move(0, 0.04, 0).cylinder(0.028, 0.028, 0.22, 0xe8eef8, 6).pop();
    b.push().move(0, 0.04, 0.16).cylinder(0.02, 0.008, 0.1, 0xfff8d0, 6).pop();
  }
  return b.mesh;
}

/** 地图道具：岩石 / 废墟 / 矿晶 */
export function buildPropMesh(kind: 'rock' | 'ore' | 'scrap' | 'barrel' | 'ruin' | 'spire', seed: number): Mesh {
  const b = new MeshBuilder();
  const r = (n: number) => {
    const x = Math.sin(seed * 12.9898 + n * 78.233) * 43758.5453;
    return x - Math.floor(x);
  };
  if (kind === 'rock') {
    const h = 0.35 + r(1) * 0.55;
    const s = 0.55 + r(2) * 0.5;
    b.box(s, h * 0.55, s * 0.85, C.concDeep);
    b.push().move(s * 0.15, h * 0.5, -s * 0.05).box(s * 0.7, h * 0.55, s * 0.6, C.concDark).pop();
    if (r(3) > 0.4) b.push().move(-s * 0.2, 0, s * 0.2).box(s * 0.4, h * 0.35, s * 0.4, C.concMid).pop();
    return b.mesh;
  }
  if (kind === 'ore') {
    const n = 3 + (r(1) * 3) | 0;
    for (let i = 0; i < n; i++) {
      const ang = (i / n) * Math.PI * 2 + r(i + 2);
      const rad = 0.12 + r(i + 8) * 0.1;
      const h = 0.18 + r(i + 5) * 0.28;
      b.push()
        .move(Math.cos(ang) * 0.18, 0, Math.sin(ang) * 0.18)
        .yaw(ang)
        .box(rad, h, rad, i % 2 === 0 ? C.gold : C.ore)
        .pop();
    }
    b.push().move(0, 0, 0).box(0.35, 0.08, 0.35, 0x6a5a38).pop();
    return b.mesh;
  }
  if (kind === 'scrap') {
    b.push().move(0, 0, 0).box(0.35, 0.08, 0.28, C.rust).pop();
    b.push().move(0.08, 0.08, -0.04).box(0.22, 0.12, 0.18, C.steelDark).pop();
    b.push().move(-0.1, 0.08, 0.08).yaw(0.4).box(0.18, 0.06, 0.22, C.pipe).pop();
    return b.mesh;
  }
  if (kind === 'barrel') {
    b.cylinder(0.12, 0.12, 0.32, r(1) > 0.5 ? C.rust : C.warn, 7);
    b.push().move(0, 0.14, 0).cylinder(0.125, 0.125, 0.05, C.black, 7).pop();
    return b.mesh;
  }
  if (kind === 'ruin') {
    b.box(0.55, 0.12, 0.4, C.concDark);
    b.push().move(-0.12, 0.12, 0).box(0.18, 0.45, 0.38, C.concMid).pop();
    b.push().move(0.16, 0.12, -0.05).box(0.16, 0.28, 0.22, C.concDeep).pop();
    return b.mesh;
  }
  // spire：裂隙晶柱
  b.push().move(0, 0, 0).box(0.18, 0.7, 0.18, 0x6a4aaa).pop();
  b.push().move(0.08, 0.2, 0.06).box(0.1, 0.45, 0.1, 0x9b7ae0).pop();
  b.push().move(-0.1, 0.1, -0.08).box(0.08, 0.35, 0.08, 0x4a3278).pop();
  return b.mesh;
}
